<template>
  <div class="daily-event-analysis" :class="{ 'is-empty-only': isEmptyOnly }">
    <template v-if="!isEmptyOnly">
      <div class="stat-details">
        <div class="stat-item">
          <div class="stat-number">{{ yearStats.thisYear }}</div>
          <div class="stat-desc">{{ statLabels.first }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">{{ yearStats.lastYear }}</div>
          <div class="stat-desc">{{ statLabels.second }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">{{ yearStats.total }}</div>
          <div class="stat-desc">{{ statLabels.third }}</div>
        </div>
      </div>
      <div class="pie-chart-container">
        <div v-if="hasChartData" ref="pieChart" class="pie-chart"></div>
        <div v-else class="chart-empty">暂无触发事件</div>
      </div>
    </template>
    <div v-else class="empty-only">暂无触发事件</div>

    <!-- 图片预览 -->
    <div v-if="showImageModal" class="modal-overlay" @click="closeImageModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <template v-if="currentItem">
              <span>创建时间：{{ currentItem.createtime || '-' }}</span>
              <span>事件类型：{{ currentItem.event_name || '-' }}</span>
            </template>
            <span>（{{ filteredMatchedEvents.length }}条）</span>
          </h3>
          <span class="close-btn" @click="closeImageModal">&times;</span>
        </div>
        <div class="modal-body">
          <div v-if="enableProjectSearch" class="project-search-bar">
            <input
              v-model="projectSearchKeyword"
              type="text"
              class="project-search-input"
              placeholder="搜索项目地址"
              @input="onProjectSearchChange"
            />
          </div>
          <div v-if="carouselItems.length" class="carousel-section">
            <div class="carousel-container">
              <button
                class="carousel-btn"
                :disabled="currentImageIndex === 0"
                @click="prevImage"
              >
                ‹
              </button>
              <div class="carousel-image-wrapper">
                <img
                  v-if="currentItem && currentItem.url"
                  :src="currentItem.url"
                  alt="事件图片"
                  class="carousel-image"
                />
                <div v-else class="no-image-placeholder">该类型暂无图片</div>
                <div class="image-counter">
                  {{ currentImageIndex + 1 }} / {{ carouselItems.length }}
                </div>
                <div
                  v-if="enableProjectSearch && currentItem && currentItem.community_name"
                  class="project-name-tip"
                >
                  项目地址：{{ currentItem.community_name }}
                </div>
              </div>
              <button
                class="carousel-btn"
                :disabled="currentImageIndex >= carouselItems.length - 1"
                @click="nextImage"
              >
                ›
              </button>
            </div>
          </div>
          <div v-else class="empty-tip">
            {{ emptyModalText }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { filterEventsByChartItem } from '@/config/dailyEventConfig'

export default {
  name: 'DailyEventAnalysis',
  props: {
    chartData: {
      type: Array,
      default: () => []
    },
    events: {
      type: Array,
      default: () => []
    },
    yearStats: {
      type: Object,
      default: () => ({
        thisYear: 0,
        lastYear: 0,
        total: 0
      })
    },
    statLabels: {
      type: Object,
      default: () => ({
        first: '今日累计触发事件',
        second: '昨日累计触发事件',
        third: '总触发事件数'
      })
    },
    /** 日管控：无事件时仅展示空状态文案 */
    emptyHideStats: {
      type: Boolean,
      default: false
    },
    /** 周排查/月调度：弹窗内按项目地址搜索 */
    enableProjectSearch: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chart: null,
      showImageModal: false,
      activeType: null,
      currentImageIndex: 0,
      projectSearchKeyword: ''
    }
  },
  computed: {
    hasChartData() {
      return this.chartData.some((item) => item.value > 0)
    },
    isEmptyOnly() {
      return this.emptyHideStats && !this.hasChartData
    },
    activeTypeLabel() {
      return this.activeType ? this.activeType.label : ''
    },
    matchedEvents() {
      if (!this.activeType) return []
      return filterEventsByChartItem(this.events, this.activeType)
    },
    filteredMatchedEvents() {
      const keyword = this.projectSearchKeyword.trim().toLowerCase()
      if (!this.enableProjectSearch || !keyword) {
        return this.matchedEvents
      }
      return this.matchedEvents.filter((evt) => {
        const projectName = String(evt.community_name || '').toLowerCase()
        return projectName.includes(keyword)
      })
    },
    carouselItems() {
      const items = []
      this.filteredMatchedEvents.forEach((evt) => {
        const meta = {
          createtime: evt.createtime || '',
          event_name: evt.event_name || this.activeTypeLabel || '',
          community_name: evt.community_name || ''
        }
        const raw = evt.picture_file || evt.picture || ''
        const urls = raw
          ? String(raw)
              .split(',')
              .map((url) => url.trim())
              .filter(Boolean)
          : []

        if (urls.length) {
          urls.forEach((url) => items.push({ url, ...meta }))
        } else {
          items.push({ url: '', ...meta })
        }
      })
      return items
    },
    currentItem() {
      return this.carouselItems[this.currentImageIndex] || null
    },
    emptyModalText() {
      if (!this.matchedEvents.length) return '该类型暂无事件记录'
      if (this.enableProjectSearch && this.projectSearchKeyword.trim()) {
        return '未找到匹配项目的事件'
      }
      return '该类型暂无事件记录'
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.$nextTick(() => this.syncChart())
      }
    },
    isEmptyOnly() {
      this.$nextTick(() => this.syncChart())
    }
  },
  mounted() {
    this.$nextTick(() => this.syncChart())
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeChart()
  },
  methods: {
    handleResize() {
      if (this.chart) this.chart.resize()
    },
    disposeChart() {
      if (!this.chart) return
      this.chart.off('click', this.onChartClick)
      this.chart.dispose()
      this.chart = null
    },
    syncChart() {
      if (this.isEmptyOnly || !this.hasChartData) {
        this.disposeChart()
        return
      }
      this.$nextTick(() => {
        if (!this.$refs.pieChart) return
        if (!this.chart) {
          this.chart = echarts.init(this.$refs.pieChart)
          this.chart.on('click', this.onChartClick)
        }
        this.updateChart()
      })
    },
    updateChart() {
      if (!this.chart || !this.hasChartData) return

      const seriesData = this.chartData
        .filter((item) => item.value > 0)
        .map((item) => ({
          ...item,
          label: {
            color: item.itemStyle?.color || '#00d4ff',
            fontSize: 11,
            fontWeight: 'bold',
            lineHeight: 14
          },
          labelLine: {
            lineStyle: {
              color: item.itemStyle?.color || '#00d4ff',
              width: 1.5
            }
          }
        }))

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const count = params.value || 0
            return `${params.name}: ${count}次 (${params.percent}%)`
          },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#00d4ff',
          textStyle: { color: '#fff' }
        },
        series: [
          {
            name: '事件分析',
            type: 'pie',
            radius: ['45%', '75%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 5,
              borderColor: '#0a1929',
              borderWidth: 2
            },
            label: {
              show: true,
              position: 'outside',
              formatter(params) {
                const name =
                  params.name.length > 4
                    ? `${params.name.substring(0, 4)}...`
                    : params.name
                return `${name}\n${params.percent}%`
              }
            },
            labelLine: {
              show: true,
              length: 12,
              length2: 8,
              smooth: 0.3
            },
            data: seriesData
          }
        ],
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '42%',
            style: {
              text: '事件分析',
              textAlign: 'center',
              fill: '#00d4ff',
              fontSize: 16,
              fontWeight: 'bold'
            }
          },
          {
            type: 'text',
            left: 'center',
            top: '58%',
            style: {
              text: '百分比',
              textAlign: 'center',
              fill: '#88c4ff',
              fontSize: 12
            }
          }
        ]
      }

      this.chart.setOption(option, true)
    },
    onChartClick(params) {
      const key = params.data && params.data.key
      if (!key) return
      const typeItem =
        this.chartData.find((item) => item.key === key) || params.data
      this.activeType = typeItem
      this.currentImageIndex = 0
      this.projectSearchKeyword = ''
      this.showImageModal = true
    },
    closeImageModal() {
      this.showImageModal = false
      this.activeType = null
      this.currentImageIndex = 0
      this.projectSearchKeyword = ''
    },
    onProjectSearchChange() {
      this.currentImageIndex = 0
    },
    prevImage() {
      if (this.currentImageIndex > 0) this.currentImageIndex -= 1
    },
    nextImage() {
      if (this.currentImageIndex < this.carouselItems.length - 1) {
        this.currentImageIndex += 1
      }
    }
  }
}
</script>

<style scoped lang="less">
.daily-event-analysis {
  display: flex;
  gap: 12px;
  min-height: 220px;
}

.stat-details {
  width: 130px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.stat-item {
  padding: 8px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 136, 204, 0.1));
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  border-left: 3px solid #00d4ff;
}

.stat-number {
  font-size: 22px;
  font-weight: bold;
  color: #00d4ff;
  line-height: 1.2;
}

.stat-desc {
  font-size: 11px;
  color: #88c4ff;
  margin-top: 4px;
  line-height: 1.3;
}

.pie-chart-container {
  flex: 1;
  min-width: 0;
  min-height: 220px;
  position: relative;
}

.pie-chart {
  width: 100%;
  height: 220px;
}

.chart-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #88c4ff;
  font-size: 14px;
}

.daily-event-analysis.is-empty-only {
  min-height: 220px;
  align-items: center;
  justify-content: center;
}

.empty-only {
  width: 100%;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #88c4ff;
  font-size: 14px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background: linear-gradient(135deg, #0a1929 0%, #1a2332 50%, #0e2a42 100%);
  border: 1px solid rgba(0, 212, 255, 0.5);
  border-radius: 10px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);

  h3 {
    color: #00d4ff;
    margin: 0;
    font-size: 16px;
  }

  .modal-title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
    line-height: 1.5;
  }
}

.close-btn {
  color: #00d4ff;
  font-size: 24px;
  cursor: pointer;
}

.modal-body {
  padding: 20px;
}

.project-search-bar {
  margin-bottom: 14px;
}

.project-search-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  background: rgba(0, 212, 255, 0.08);
  border: 1px solid rgba(0, 212, 255, 0.35);
  border-radius: 6px;
  color: #e8f4ff;
  font-size: 14px;
  outline: none;

  &::placeholder {
    color: #88c4ff;
  }

  &:focus {
    border-color: rgba(0, 212, 255, 0.6);
    box-shadow: 0 0 8px rgba(0, 212, 255, 0.15);
  }
}

.project-name-tip {
  margin-top: 8px;
  color: #88c4ff;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
}

.carousel-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.carousel-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.carousel-btn {
  width: 36px;
  height: 36px;
  border: 1px solid rgba(0, 212, 255, 0.4);
  background: rgba(0, 212, 255, 0.1);
  color: #00d4ff;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.carousel-image-wrapper {
  flex: 1;
  text-align: center;
}

.carousel-image {
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid rgba(0, 212, 255, 0.3);
}

.image-counter {
  margin-top: 10px;
  color: #88c4ff;
  font-size: 12px;
}

.no-image-placeholder {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #88c4ff;
  font-size: 14px;
  border: 1px dashed rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  background: rgba(0, 212, 255, 0.05);
}

.empty-tip {
  text-align: center;
  color: #88c4ff;
  padding: 24px;
}
</style>
